import clsx from "clsx";
import React, { FC, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Updater } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgPaper,
  IgTypography,
} from "@intelligems/ig-theme";
import { CouponType, OrganizationSubscriptionDto } from "@intelligems/ig-types";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useGetBillingUrl } from "../../api/subscriptions/get-billing-url";
import theme from "../../theme";
import { pricingPage } from "../../utils/linkSheet";
import openNewTab from "../../utils/openNewTab";

import { AvailableSubscriptionTypeFe } from "./SubscriptionPriceSection";

const PREFIX = "SubscriptionPlan";

const classes = {
  paper: `${PREFIX}-paper`,
  paperSelected: `${PREFIX}-paperSelected`,
  paperGrid: `${PREFIX}-paperGrid`,
  planHeader: `${PREFIX}-planHeader`,
  planHeaderWithText: `${PREFIX}-planHeaderWithText`,
  planHeaderCurrent: `${PREFIX}-planHeaderCurrent`,
  title: `${PREFIX}-title`,
  monthlyPrice: `${PREFIX}-monthlyPrice`,
  annualPrice: `${PREFIX}-annualPrice`,
  comparePrice: `${PREFIX}-compare-price`,
  annualComparePrice: `${PREFIX}-annual-compare-price`,
  interval: `${PREFIX}-interval`,
  description: `${PREFIX}-description`,
  footerText: `${PREFIX}-footerText`,
  footerButton: `${PREFIX}-footerButton`,
  footerButtonCurrent: `${PREFIX}-footerButtonCurrent`,
  footerButtonOther: `${PREFIX}-footerButtonOther`,
  selected: `${PREFIX}-selected`,
  disabled: `${PREFIX}-disabled`,
};

const StyledPaper = styled(IgPaper)(({ theme }) => ({
  paddingTop: "0px",
  paddingLeft: "30px",
  paddingRight: "30px",
  paddingBottom: "18px",
  backgroundColor: "white",
  height: "101%",
  borderWidth: "1px !important",
  borderColor: "#909090",
  minWidth: "250px",
  boxShadow: "none",

  [`&.${classes.paperSelected}`]: {
    borderColor: `${theme.palette.primary.dark} !important`,
    borderWidth: "2px !important",
  },
  [`& .${classes.paperGrid}`]: {
    height: "100%",
    minHeight: "400px",
    flexWrap: "nowrap",
  },

  [`& .${classes.selected}`]: {
    color: theme.palette.primary.dark,
  },

  [`& .${classes.disabled}`]: {
    color: theme.palette.disabled.main,
  },

  [`& .${classes.planHeader}`]: {
    padding: 5,
    borderRadius: "11px 11px 0px 0px",
    marginLeft: -30,
    marginTop: -1,
    marginRight: -30,
    color: "white",
    height: "24px",
  },

  [`& .${classes.planHeaderWithText}`]: {
    background:
      "linear-gradient(180deg, #0C38B1 0%, #2656D4 52.08%, #3E71F5 100%)",
  },
  [`& .${classes.planHeaderCurrent}`]: {
    background: "linear-gradient(180deg, #2CAF2C 0%, #6EDA6E 97.92%)",
    borderRadius: "12px 12px 0px 0px;",
  },

  [`& .${classes.title}`]: {
    paddingTop: "10px",
    marginBottom: "-9px",
  },

  [`& .${classes.monthlyPrice}`]: {
    fontSize: "40px",
  },
  [`& .${classes.annualPrice}`]: {
    fontSize: "32px",
  },
  [`& .${classes.comparePrice}`]: {
    fontSize: "40px",
    textDecoration: "line-through",
    fontWeight: 400,
    color: theme.palette.disabled.dark,
  },
  [`& .${classes.annualComparePrice}`]: {
    fontSize: "32px",
    textDecoration: "line-through",
    fontWeight: 400,
    color: theme.palette.disabled.dark,
  },

  [`& .${classes.interval}`]: { marginTop: "-10px" },
  [`& .${classes.description}`]: {
    // height: "200px"
  },

  [`& .${classes.footerText}`]: {
    textAlign: "center",
    paddingTop: "0px",
    color: theme.palette.primary.main,
  },

  [`& .${classes.footerButton}`]: {
    width: "100%",
  },

  [`& .${classes.footerButtonCurrent}`]: {
    backgroundColor: theme.palette.primary.main,
  },

  [`& .${classes.footerButtonOther}`]: {
    backgroundColor: theme.palette.primary.dark,
  },
}));

interface SubscriptionPlanProps {
  subscription: AvailableSubscriptionTypeFe;
  orgSubscriptions?: OrganizationSubscriptionDto[];
  addonSubscription?: AvailableSubscriptionTypeFe;
  availableSubscriptions: AvailableSubscriptionTypeFe[];
  setAvailableSubscriptions: Updater<AvailableSubscriptionTypeFe[]>;
  isBundleCurrent: boolean;
  buttonText: string;
  coupon?: CouponType;
}

export const SubscriptionPlanBox = ({
  orgSubscriptions,
  subscription,
  buttonText,
  addonSubscription,
  availableSubscriptions,
  setAvailableSubscriptions,
  isBundleCurrent,
  coupon,
}: SubscriptionPlanProps) => {
  const approveBilling = useGetBillingUrl();

  const orgSubscription = (orgSubscriptions || []).find((sub) => {
    return sub.subscriptionId === subscription.id;
  });

  const headerType = useMemo(() => {
    if (isBundleCurrent && subscription.id === "bundle") {
      return "current";
    }
    if (isBundleCurrent && orgSubscription) {
      return "alt";
    }
    if (orgSubscription) {
      return "current";
    }
    return "alt";
  }, [isBundleCurrent, subscription, orgSubscription]);

  const price = useMemo(() => {
    let p =
      orgSubscription?.subscriptionPriceUsd ||
      subscription.bundlePrice ||
      subscription.defaultPrice;

    if (coupon && headerType !== "current") {
      if (coupon.amountOff) {
        p = p - coupon.amountOff / 100;
      } else if (coupon.percentOff) {
        const amountOff = (p * coupon.percentOff) / 100;
        p = p - amountOff;
      }
    }

    if (
      (orgSubscription?.subscriptionInterval ||
        subscription.defaultInterval) === "annual"
    ) {
      return Number((p / 12).toFixed(2));
    }
    return p;
  }, [subscription, orgSubscriptions]);

  const compareAtPrice = useMemo(() => {
    let c: number | undefined = undefined;

    if (
      orgSubscription &&
      (orgSubscription.subscriptionPriceUsd || 0 < subscription.defaultPrice)
    ) {
      c = subscription.defaultPrice || subscription.bundlePrice;
    } else if (subscription.defaultComparePrice) {
      c = subscription.defaultComparePrice;
    }

    if (coupon && headerType !== "current") {
      c = subscription.defaultComparePrice || subscription.defaultPrice;
    }

    if (c) {
      if (
        (orgSubscription?.subscriptionInterval ||
          subscription.defaultInterval) === "annual"
      ) {
        c = Number((c / 12).toFixed(2));
      }

      if (price < c) {
        return c;
      }
    }

    return null;
  }, [subscription, orgSubscriptions, price]);

  const bundleIndex = availableSubscriptions.findIndex(
    (sub) => sub.id === "bundle"
  )!;

  const isBundleSelected =
    bundleIndex > -1 && availableSubscriptions[bundleIndex].isSelected === true;

  const shouldHighlight = useMemo(() => {
    if (isBundleSelected && subscription.id === "bundle") {
      return true;
    }
    if (isBundleSelected && subscription.id !== "bundle") {
      return false;
    }
    return subscription.isSelected;
  }, [isBundleSelected, availableSubscriptions]);

  const shouldDisable = useMemo(() => {
    if (subscription.id === "bundle") {
      return false;
    }
    return isBundleSelected && subscription.id !== "bundle";
  }, [isBundleSelected, availableSubscriptions]);

  const colorOverride = useMemo(() => {
    if (shouldHighlight) {
      return classes.selected;
    } else if (shouldDisable) {
      return classes.disabled;
    } else {
      return "";
    }
  }, [shouldHighlight, shouldDisable]);

  const onAddonClick = () => {
    setAvailableSubscriptions((draft) => {
      const addonIndex = draft.findIndex((sub) => sub.name.includes("ADDON"));
      if (addonIndex !== -1) {
        draft[addonIndex].isSelected = !draft[addonIndex].isSelected;
      }
    });
  };

  const onButtonClick = (id: string) => {
    setAvailableSubscriptions((draft) => {
      for (const sub of draft) {
        sub.isSelected = sub.id === id;
      }

      const nonBundles = draft.filter((i) => i.id !== "bundle");
      if (nonBundles.every((sub) => sub.isSelected)) {
        draft.forEach((sub) => {
          sub.isSelected = true;
        });
      }

      const pricingSub = draft.find((sub) => sub.name.includes("PRICING"));
      if (pricingSub && pricingSub!.isSelected) {
        const shippingIndex = draft.findIndex((sub) =>
          sub.name.includes("SHIPPING")
        );
        if (shippingIndex !== -1) {
          draft[shippingIndex].isSelected = false;
        }
      }
      return draft;
    });
  };

  const buttonInfo = useMemo(() => {
    if (isBundleSelected) {
      if (subscription.name.includes("SHIPPING")) {
        return {
          text: "Unavailable",
          icon: undefined,
          backgroundColor: `${theme.palette.disabled.main} !important`,
        };
      }
      if (subscription.id === "bundle") {
        return {
          text: "Selected",
          icon: <CheckIcon />,
          backgroundColor: theme.palette.primary.dark,
        };
      } else {
        return {
          text: "Selected",
          icon: <CheckIcon />,
          backgroundColor: `${theme.palette.disabled.main} !important`,
        };
      }
    }
    const pricingSub = availableSubscriptions.find((sub) =>
      sub.name.includes("PRICING")
    );

    if (pricingSub && pricingSub.isSelected) {
      if (subscription.name.includes("SHIPPING")) {
        return {
          text: "Unavailable",
          icon: undefined,
          backgroundColor: `${theme.palette.disabled.main} !important`,
        };
      }
    }
    if (subscription.isSelected) {
      return {
        text: "Selected",
        icon: <CheckIcon />,
        backgroundColor: theme.palette.primary.dark,
      };
    }

    // const activeSub = (orgSubscriptions || []).find(
    //   (sub) => sub.subscriptionStatus === "active"
    // );
    // if (
    //   activeSub &&
    //   (activeSub.subscriptionPriceUsd || 0) > (subscription.defaultPrice || 0)
    // ) {
    //   return {
    //     text: "Contact Sales",
    //     icon: undefined,
    //     color: "white !important",
    //     backgroundColor: `${theme.palette.disabled.main} !important`,
    //     disable: true,
    //   };
    // }

    if (subscription.id === "bundle") {
      return null;
      // return { text: "Add All Features", icon: <AddIcon />, color: undefined };
    }
    return { text: buttonText, icon: <AddIcon />, color: undefined };
  }, [subscription, orgSubscriptions]);

  return (
    <StyledPaper
      className={
        shouldHighlight
          ? clsx(classes.paper, classes.paperSelected)
          : clsx(classes.paper)
      }
    >
      <Grid
        container
        direction={"column"}
        className={classes.paperGrid}
        spacing={2}
      >
        <Grid item xs>
          <Grid container direction={"column"} rowSpacing={0.1}>
            <Grid item xs>
              <Box
                style={{ display: "flex" }}
                justifyContent={"center"}
                className={
                  headerType === "current"
                    ? clsx(
                        classes.planHeader,
                        classes.planHeaderCurrent,
                        colorOverride
                      )
                    : subscription.altHeader
                      ? clsx(
                          classes.planHeader,
                          classes.planHeaderWithText,
                          colorOverride
                        )
                      : classes.planHeader
                }
              >
                <IgTypography sx={{ color: "white" }}>
                  {headerType === "current"
                    ? "Current Plan"
                    : subscription.altHeader || ""}
                </IgTypography>
              </Box>
            </Grid>
            <Grid item xs className={classes.title}>
              <IgTypography
                className={colorOverride}
                sx={{
                  fontWeight: 600,
                }}
              >
                {subscription.title}
              </IgTypography>
            </Grid>
            <Grid
              item
              container
              direction="row"
              xs
              className={
                subscription.defaultInterval === "annual"
                  ? classes.annualPrice
                  : classes.monthlyPrice
              }
            >
              {
                <Grid item>
                  <IgTypography
                    className={clsx(
                      subscription.defaultInterval === "annual"
                        ? classes.annualPrice
                        : classes.monthlyPrice,
                      colorOverride
                    )}
                  >{`$${price.toFixed(2)}`}</IgTypography>
                </Grid>
              }
              {compareAtPrice ? (
                <Grid item sx={{ marginLeft: "20px" }}>
                  <IgTypography
                    className={clsx(
                      subscription.defaultInterval === "annual"
                        ? classes.annualComparePrice
                        : classes.comparePrice,
                      colorOverride
                    )}
                  >{`$${compareAtPrice}`}</IgTypography>
                </Grid>
              ) : null}
            </Grid>
            <Grid item xs className={classes.interval}>
              <IgTypography
                sx={{ fontWeight: 500, fontStyle: "italic" }}
                className={clsx(colorOverride)}
              >
                {subscription.defaultInterval === "monthly"
                  ? "Monthly"
                  : "Per Month"}
              </IgTypography>
            </Grid>
            {subscription.defaultInterval === "annual" ? (
              <Grid item xs>
                <IgTypography
                  style={{ fontWeight: 500 }}
                  className={clsx(colorOverride)}
                >
                  Billed at ${(Number(price) * 12).toFixed(2)}/year
                </IgTypography>
              </Grid>
            ) : null}
            <Grid item xs className={clsx(classes.description)}>
              <div className={"MuiTypography-root MuiTypography-body1"}>
                <IgTypography
                  component={"span"}
                  className={clsx(colorOverride)}
                >
                  <ReactMarkdown children={subscription.description} />
                </IgTypography>
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs className={classes.footerText} style={{ flexGrow: 0 }}>
          <Grid container direction={"column"} spacing={0}>
            {addonSubscription && subscription.isSelected ? (
              <Grid
                container
                item
                xs
                direction={"row"}
                sx={{ flexWrap: "nowrap", paddingBottom: "15px" }}
              >
                <Grid item>
                  <IgCheckbox
                    checked={addonSubscription && addonSubscription.isSelected}
                    sx={{ paddingLeft: "0px", paddingTop: "3px" }}
                    onClick={onAddonClick}
                  />
                </Grid>
                <Grid
                  item
                  sx={{
                    paddingBottom: "5px",
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  <IgTypography
                    sx={{ color: theme.palette.primary.dark, fontSize: "14px" }}
                  >
                    {addonSubscription.description}
                  </IgTypography>
                </Grid>
              </Grid>
            ) : null}
            <Grid item sx={{ marginBottom: "5px" }}>
              <IgButton
                onClick={() => {
                  openNewTab(pricingPage);
                }}
              >
                Learn More
              </IgButton>
            </Grid>
            {buttonInfo ? (
              <Grid item xs>
                <IgButton
                  loading={approveBilling.isPending}
                  variant={"contained"}
                  startIcon={buttonInfo.icon}
                  onClick={() => onButtonClick(subscription.id!)}
                  className={
                    orgSubscriptions
                      ? clsx(classes.footerButton, classes.footerButtonCurrent)
                      : classes.footerButton
                  }
                  sx={{
                    color: buttonInfo.color,
                    backgroundColor: buttonInfo.backgroundColor,
                  }}
                >
                  {buttonInfo.text}
                </IgButton>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </StyledPaper>
  );
};
